import { useCustomQuery, useGetQuery, useFindByQuery } from '../../graphql';
import { WorkspaceRoutine } from '../../routine';
import { useEmbeddedRoutines } from './useEmbeddedRoutines';
import { useWorkspaceAsset } from '../../assets/hooks/useWorkspaceAsset';
import { useMemo } from 'react';

export const useRoutineAlerts = (
  workspaceAssetId: string,
  workspaceRoutineId: string,
  productId?: string,
  options?: { enabled?: boolean }
) => {
  const isProductMode = !workspaceAssetId && !!productId;
  const enabled =
    (options?.enabled ?? true) && (!!workspaceAssetId || isProductMode);
  const assetEnabled = enabled && !isProductMode;

  const { data: workspaceAssetData, isLoading: isWorkspaceAssetIdLoading } =
    useGetQuery({
      modelName: 'workspaceasset',
      query: GET_WORKSPACE_ASSET_FOR_ASSET_ID,
      id: workspaceAssetId,
      options: { enabled: assetEnabled },
    });

  const assetId = (workspaceAssetData as any)?.asset?.id;

  const { workspaceAsset: wsAsset, isLoading: isWorkspaceAssetLoading } =
    useWorkspaceAsset(assetId || '', assetId ? false : true);

  // Scoped key avoids collision with `useWorkspaceRoutine`'s
  // `['workspaceroutine', 'details', id]`, which routine submit depends on
  // (GRIPP-2801). Keeping 'workspaceroutine' as the first segment ensures
  // routine mutations that invalidate cacheKeys('WorkspaceRoutine').all
  // also invalidate this query.
  const {
    data: workspaceRoutineResponse,
    isLoading: isWorkspaceRoutineLoading,
  } = useCustomQuery({
    cacheKey: ['workspaceroutine', 'routineAlerts', workspaceRoutineId],
    query: GET_WORKSPACE_ROUTINE,
    variables: { id: workspaceRoutineId },
    options: { enabled },
  });

  const workspaceRoutineData = (
    workspaceRoutineResponse as
      | { getWorkspaceRoutine?: WorkspaceRoutine }
      | undefined
  )?.getWorkspaceRoutine;

  const { data: routineWorkflowData, isLoading: isWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: {
        filter: isProductMode
          ? { product: { eq: productId } }
          : { workspaceAsset: { eq: workspaceAssetId } },
      },
      options: { enabled },
    });

  // Merge all routines
  const allRoutines = useMemo(() => {
    const routines = [
      ...(wsAsset?.routines || []),
      ...(wsAsset?.groups?.flatMap((g: any) => g.routines || []) || []),
      ...(wsAsset?.asset?.product?.routines || []),
    ];

    const seen = new Set<string>();
    return routines.filter((r) => r?.id && !seen.has(r.id) && seen.add(r.id));
  }, [wsAsset]);

  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } =
    useEmbeddedRoutines(allRoutines);

  const routineWorkflows = useMemo(() => {
    return (routineWorkflowData?.items ?? []).filter(
      (workflow: any) =>
        workflow?.trigger?.config?.workspaceRoutineId === workspaceRoutineId
    );
  }, [routineWorkflowData?.items, workspaceRoutineId]);

  const isLoading = isProductMode
    ? isWorkspaceRoutineLoading || isWorkflowLoading
    : isWorkspaceAssetIdLoading ||
      isWorkspaceAssetLoading ||
      isWorkspaceRoutineLoading ||
      isWorkflowLoading ||
      isEmbeddedRoutinesLoading;

  const workflowState = (workspaceAssetData as any)?.workflowState;

  const memoizedWorkspaceAsset = useMemo(() => {
    if (!wsAsset) return undefined;
    return {
      ...wsAsset,
      routines: routinesWithEmbedded,
      ...(workflowState ? { workflowState } : {}),
    };
  }, [wsAsset, routinesWithEmbedded, workflowState]);

  return {
    workspaceAsset: memoizedWorkspaceAsset,
    workspaceRoutine: workspaceRoutineData as WorkspaceRoutine,
    routineWorkflows,
    isLoading,
  };
};

const GET_WORKSPACE_ASSET_FOR_ASSET_ID = `
  query getWorkspaceAsset($id: ID!) {
    getWorkspaceAsset(id: $id) {
      id
      asset {
        id
      }
      workflowState {
        workflowId
        triggerState
        actionState
      }
    }
  }
`;

export const GET_WORKSPACE_ROUTINE = `
  query getWorkspaceRoutine($id: ID!) {
    getWorkspaceRoutine(id: $id) {
      id
      name
      workspace {
        id
      }
      type
      config
      currentVersion {
        id
      }
    }
  }
`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
}
`;
